/**
 * Empty fields and URLs: content a visitor would notice missing, or a
 * destination that goes nowhere. Advisory rather than blocking — none of these
 * prevent an asset from working, they just make it worse.
 */
import { isValidRedirectUrl } from "@/util/formSchedule";
import type { GroupField } from "@/util/methods";
import { QUICK_ADD_FIRST } from "../types";
import type {
  BuilderContext,
  ContextElement,
  RuleResult,
  ValidationRule,
} from "../types";

const ALL_ASSETS: ValidationRule["assetTypes"] = ["form", "survey", "quiz"];
/** The disqualify surface renders for legacy surveys only. */
const SURVEY_ONLY: ValidationRule["assetTypes"] = ["survey"];
/** Forms open with a usable set of fields; surveys and quizzes start blank. */
const SLIDE_ASSETS: ValidationRule["assetTypes"] = ["survey", "quiz"];

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * A destination the builder itself accepts.
 *
 * `isValidRedirectUrl` requires an explicit scheme, but the builder lets a bare
 * host like `www.abc.com` through and the visitor still lands somewhere. A
 * recommendation that calls that malformed is crying wolf, so the scheme is
 * assumed before deciding.
 */
const isUsableDestination = (url: string): boolean =>
  isValidRedirectUrl(url) ||
  (!/^[a-z][a-z0-9+.-]*:/i.test(url) && isValidRedirectUrl(`https://${url}`));

/** Element labels carry markup (an h1, a rich-text paragraph), so strip it. */
const plainText = (value: unknown): string =>
  text(value)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const elementResult = (
  ruleId: string,
  section: ValidationRule["section"],
  entry: ContextElement,
  sidebarTab?: string
): RuleResult => ({
  ruleId,
  severity: "recommendation",
  section,
  key: `${ruleId}:${entry.elementKey}`,
  target: {
    elementIndex: entry.elementIndex,
    slideIndex: entry.slideIndex,
    elementKey: entry.elementKey,
    sidebarTab,
  },
});

const settingResult = (
  ruleId: string,
  section: ValidationRule["section"],
  settingsTab: string
): RuleResult => ({
  ruleId,
  severity: "recommendation",
  section,
  key: ruleId,
  target: { settingsTab },
});

/** One rule per element predicate — they all share the same shape. */
const elementRule = (
  id: string,
  predicate: (element: GroupField) => boolean,
  section: ValidationRule["section"] = "fields",
  sidebarTab?: string
): ValidationRule => ({
  id,
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section,
  evaluate: (ctx) =>
    ctx.elements
      .filter((entry) => predicate(entry.element))
      .map((entry) => elementResult(id, section, entry, sidebarTab)),
});

/* ── Empty element content ──────────────────────────────────────────────── */

export const headerTextEmpty = elementRule(
  "header_text_empty",
  (element) =>
    (element.tag === "header" || element.tag === "text") &&
    !plainText(element.label)
);

export const htmlElementEmpty = elementRule(
  "html_element_empty",
  (element) => element.tag === "html" && !text(element.html)
);

export const buttonTextEmpty = elementRule(
  "button_text_empty",
  (element) => element.tag === "button" && !plainText(element.label)
);

export const sourceFieldValueEmpty = elementRule(
  "source_field_value_empty",
  (element) => element.tag === "source" && !text(element.value)
);

const optionLabels = (element: GroupField): string[] => {
  const options = element.picklistOptions;
  if (!Array.isArray(options)) return [];
  return options.map((option) =>
    text(typeof option === "string" ? option : option?.label)
  );
};

// An option's label is edited on the Options tab, not Content.
export const optionLabelEmpty = elementRule(
  "option_label_empty",
  (element) => optionLabels(element).some((label) => !label),
  "fields",
  "Options"
);

/**
 * The control is labelled "Max file limit" and stores a file *count* on the
 * misleadingly named `maxFileSize`. It has no type, min or validator, so a
 * blank entry ships NaN.
 */
export const fileUploadMaxSizeInvalid = elementRule(
  "file_upload_max_size_invalid",
  (element) => {
    const upload = element as { isMultipleFile?: boolean; maxFileSize?: unknown };
    if (element.dataType !== "FILE_UPLOAD" || !upload.isMultipleFile) return false;
    const limit = Number.parseInt(String(upload.maxFileSize ?? ""), 10);
    return Number.isNaN(limit) || limit < 1;
  }
);

/* ── Destinations ───────────────────────────────────────────────────────── */

const SUBMIT_ACTION_URL = "1";

export const submitRedirectUrlMissing: ValidationRule = {
  id: "submit_redirect_url_missing",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) => {
    if (String(ctx.formAction.actionType ?? "") !== SUBMIT_ACTION_URL) return [];
    return text(ctx.formAction.redirectUrl)
      ? []
      : [settingResult("submit_redirect_url_missing", "settings", "settings")];
  },
};

export const submitRedirectUrlInvalid: ValidationRule = {
  id: "submit_redirect_url_invalid",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) => {
    if (String(ctx.formAction.actionType ?? "") !== SUBMIT_ACTION_URL) return [];
    const url = text(ctx.formAction.redirectUrl);
    // A blank URL is the other rule's finding; this one is about malformed.
    if (!url || isUsableDestination(url)) return [];
    return [settingResult("submit_redirect_url_invalid", "settings", "settings")];
  },
};

const DISQUALIFY_URL = 1;

export const disqualifyUrlMissing: ValidationRule = {
  id: "disqualify_url_missing",
  severity: "recommendation",
  assetTypes: SURVEY_ONLY,
  section: "settings",
  evaluate: (ctx) => {
    if (Number(ctx.formAction.endsurveyType ?? 0) !== DISQUALIFY_URL) return [];
    return text(ctx.formAction.disqualifiedUrl)
      ? []
      : [settingResult("disqualify_url_missing", "settings", "settings")];
  },
};

export const disqualifyMessageEmpty: ValidationRule = {
  id: "disqualify_message_empty",
  severity: "recommendation",
  assetTypes: SURVEY_ONLY,
  section: "settings",
  evaluate: (ctx) => {
    if (Number(ctx.formAction.endsurveyType ?? 0) === DISQUALIFY_URL) return [];
    return plainText(ctx.formAction.disqualifiedText)
      ? []
      : [settingResult("disqualify_message_empty", "settings", "settings")];
  },
};

/* ── Schedule & visibility ──────────────────────────────────────────────── */

interface VisibilityStateLike {
  mode?: string;
  html?: string;
  redirectUrl?: string;
}

const visibilityStates = (ctx: BuilderContext): VisibilityStateLike[] => {
  const schedule = ctx.builderConfig.formSchedule as
    | { enabled?: boolean; states?: Record<string, VisibilityStateLike> }
    | undefined;
  if (!schedule?.enabled) return [];
  return Object.values(schedule.states ?? {});
};

export const scheduleRedirectUrlInvalid: ValidationRule = {
  id: "schedule_redirect_url_invalid",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) => {
    const broken = visibilityStates(ctx).some((state) => {
      if (state.mode !== "redirect") return false;
      const url = text(state.redirectUrl);
      return Boolean(url) && !isUsableDestination(url);
    });
    return broken
      ? [settingResult("schedule_redirect_url_invalid", "settings", "settings")]
      : [];
  },
};

/** A state with neither a page nor a destination shows the visitor nothing. */
export const scheduleNoClosedStateContent: ValidationRule = {
  id: "schedule_no_closed_state_content",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) => {
    const empty = visibilityStates(ctx).some((state) =>
      state.mode === "redirect"
        ? !text(state.redirectUrl)
        : !plainText(state.html)
    );
    return empty
      ? [
          settingResult(
            "schedule_no_closed_state_content",
            "settings",
            "settings"
          ),
        ]
      : [];
  },
};

/* ── Notifications ──────────────────────────────────────────────────────── */

const recipients = (ctx: BuilderContext): string[] => {
  const list = (ctx.emailNotificationsConfig as { emailTo?: unknown } | null)
    ?.emailTo;
  return Array.isArray(list) ? list.map(text) : [];
};

export const notificationRecipientMissing: ValidationRule = {
  id: "notification_recipient_missing",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) => {
    if (ctx.builderConfig.emailNotifications !== true) return [];
    return recipients(ctx).filter(Boolean).length
      ? []
      : [
          settingResult(
            "notification_recipient_missing",
            "settings",
            "notifications"
          ),
        ];
  },
};

/** Deliberately loose: a local-part, an @, and a dotted domain. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const notificationEmailInvalid: ValidationRule = {
  id: "notification_email_invalid",
  severity: "recommendation",
  assetTypes: ALL_ASSETS,
  section: "settings",
  evaluate: (ctx) => {
    if (ctx.builderConfig.emailNotifications !== true) return [];
    const broken = recipients(ctx).some(
      (address) => Boolean(address) && !EMAIL.test(address)
    );
    return broken
      ? [
          settingResult(
            "notification_email_invalid",
            "settings",
            "notifications"
          ),
        ]
      : [];
  },
};

/**
 * A survey or quiz with nothing on it saves happily and submits nothing. The
 * builder gives no other prompt, so the first question is worth asking for.
 */
export const assetHasNoElements: ValidationRule = {
  id: "asset_has_no_elements",
  severity: "recommendation",
  assetTypes: SLIDE_ASSETS,
  section: "questions",
  evaluate: (ctx) =>
    ctx.elements.length
      ? []
      : [
          {
            ruleId: "asset_has_no_elements",
            severity: "recommendation",
            section: "questions",
            key: "asset_has_no_elements",
            target: { settingsTab: "elements", quickAddTag: QUICK_ADD_FIRST },
          },
        ],
};

export const contentRecommendationRules: ValidationRule[] = [
  assetHasNoElements,
  headerTextEmpty,
  htmlElementEmpty,
  buttonTextEmpty,
  sourceFieldValueEmpty,
  optionLabelEmpty,
  fileUploadMaxSizeInvalid,
  submitRedirectUrlMissing,
  submitRedirectUrlInvalid,
  disqualifyUrlMissing,
  disqualifyMessageEmpty,
  scheduleRedirectUrlInvalid,
  scheduleNoClosedStateContent,
  notificationRecipientMissing,
  notificationEmailInvalid,
];
